/**
 * v4.39.0 — Review Shield status + SLA aging chips.
 *
 * Presentation-only atoms over the EXISTING `status` and `timestamp`
 * fields. No database changes, no new API endpoints, no schema mutations.
 *
 * Renders:
 *   • <StatusChip /> — derives one of three states from
 *       (status, contact_requested):
 *         status === "closed"                       → 🟢 Resolved
 *         status === "open" && contact_requested    → 🟡 Awaiting Reply
 *         status === "open" && !contact_requested   → ⚪ No Contact Requested
 *
 *   • <SLAChip /> — computes age from `timestamp` and buckets:
 *         < 24h     → 🟢 New
 *         24h – 48h → 🟡 Aging
 *         > 48h     → 🔴 Overdue
 *
 * Both atoms accept `data-insight-id` so Insight Mode can attach the
 * Guidance Registry entries authored in
 * `backend/operational_base/review_shield_v439_seed.py`.
 */
import { useTranslation } from 'react-i18next';

const TOKEN = {
    resolved:     { bg: 'rgba(48, 227, 140, 0.10)', ring: 'rgba(48, 227, 140, 0.45)', fg: '#30E38C' },
    awaiting:     { bg: 'rgba(255, 200, 55, 0.10)', ring: 'rgba(255, 200, 55, 0.45)', fg: '#FFC837' },
    no_contact:   { bg: 'rgba(180, 190, 210, 0.12)', ring: 'rgba(180, 190, 210, 0.35)', fg: '#94A4BF' },
    sla_new:      { bg: 'rgba(48, 227, 140, 0.10)', ring: 'rgba(48, 227, 140, 0.45)', fg: '#30E38C' },
    sla_aging:    { bg: 'rgba(255, 200, 55, 0.10)', ring: 'rgba(255, 200, 55, 0.45)', fg: '#FFC837' },
    sla_overdue:  { bg: 'rgba(255, 75, 87, 0.10)',  ring: 'rgba(255, 75, 87, 0.45)',  fg: '#FF4B57' },
};

const Chip = ({ tone, icon, label, testId, insightId }) => (
    <span
        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap"
        style={{ background: tone.bg, borderColor: tone.ring, color: tone.fg }}
        data-testid={testId}
        data-insight-id={insightId}
    >
        <span aria-hidden="true">{icon}</span>
        <span>{label}</span>
    </span>
);

// Closed rows include both "closed" and "resolved" (the actual persisted
// enum). Use this helper anywhere SLA needs to skip rendering for a
// finalized conversation.
const _isClosed = (row) => {
    const s = row?.status || 'open';
    return s === 'closed' || s === 'resolved';
};

/**
 * Derive the visual status chip from the row's data.
 * Returns null when the row is in an unexpected combined state — calmer
 * to render nothing than to mislabel.
 */
export const StatusChip = ({ row }) => {
    const { t } = useTranslation();
    if (!row) return null;
    const status = row.status || 'open';
    const requested = !!row.contact_requested;

    // The actual persisted enum is `open` | `resolved`. The product spec
    // uses "closed" as the human-facing synonym for resolved; we treat
    // both equivalently here so the chip stays accurate regardless of
    // future schema evolution.
    const isClosed = status === 'closed' || status === 'resolved';
    if (isClosed) {
        return <Chip tone={TOKEN.resolved} icon="🟢" label={t('reviewShield.chip.resolved')}
                     testId="rs-chip-resolved" insightId="rs.chip.resolved" />;
    }
    if (requested) {
        return <Chip tone={TOKEN.awaiting} icon="⏳" label={t('reviewShield.chip.awaiting_reply')}
                     testId="rs-chip-awaiting-reply" insightId="rs.chip.awaiting_reply" />;
    }
    return <Chip tone={TOKEN.no_contact} icon="⚪" label={t('reviewShield.chip.no_contact')}
                 testId="rs-chip-no-contact" insightId="rs.chip.no_contact" />;
};

/**
 * SLA aging chip computed client-side from row.timestamp. No backend
 * timers, no schema fields — the clock is the source of truth.
 *
 * Returns null when no timestamp is parseable so the row stays calm
 * rather than showing a misleading "Overdue" on bad data.
 */
export const SLAChip = ({ row }) => {
    const { t } = useTranslation();
    if (!row?.timestamp) return null;
    const tsMs = Date.parse(row.timestamp);
    if (Number.isNaN(tsMs)) return null;
    const ageHours = (Date.now() - tsMs) / 3_600_000;

    // Closed rows don't need an SLA badge — the conversation is over.
    if (_isClosed(row)) return null;

    if (ageHours < 24) {
        return <Chip tone={TOKEN.sla_new} icon="🟢" label={t('reviewShield.chip.aging.new')}
                     testId="rs-chip-aging-new" insightId="rs.chip.aging.new" />;
    }
    if (ageHours < 48) {
        return <Chip tone={TOKEN.sla_aging} icon="🟡" label={t('reviewShield.chip.aging.aging')}
                     testId="rs-chip-aging-aging" insightId="rs.chip.aging.aging" />;
    }
    return <Chip tone={TOKEN.sla_overdue} icon="🔴" label={t('reviewShield.chip.aging.overdue')}
                 testId="rs-chip-aging-overdue" insightId="rs.chip.aging.overdue" />;
};

export { TOKEN };
export default { StatusChip, SLAChip };
